import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ClienteLayout from '../../components/ClienteLayout'
import { puedoDarDeBaja, darDeBajaMiCuenta } from '../../lib/portalClienteApi'
import { supabase } from '../../lib/supabase'

/**
 * /cliente/dar-de-baja
 *
 * Cualquier usuario puede pedir dar de baja la cuenta, PERO hay tres
 * bloqueantes operativos:
 *   1. Tener una suscripción activa o pausada → primero cancelarla.
 *   2. Tener un pedido en D-1 (kit ya armado, sale hoy/mañana) → esperar.
 *   3. Tener un pedido pendiente de pago → pagar o cancelar primero.
 *
 * Si está libre, hacemos soft delete (profiles.cuenta_baja_at) — no
 * eliminamos auth.users para mantener auditoría. Al próximo login el
 * RequireAuth chequea el flag y muestra mensaje de reactivación.
 */
const MOTIVOS_SUGERIDOS = [
  'No estoy usando la cuenta',
  'Los productos no me convencieron',
  'Es muy caro para mí',
  'Me mudo / cambié de barrio',
  'Tuve un problema con un pedido',
  'Otro motivo',
]

function fmtFecha(iso) {
  if (!iso) return ''
  const d = new Date(iso + (iso.length === 10 ? 'T12:00:00' : ''))
  const dias = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
  const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
  return `${dias[d.getDay()]} ${d.getDate()} de ${meses[d.getMonth()]}`
}

export default function DarDeBaja() {
  const navigate = useNavigate()
  const [cargando, setCargando] = useState(true)
  const [chequeo, setChequeo] = useState(null)
  const [motivo, setMotivo] = useState('')
  const [motivoOtro, setMotivoOtro] = useState('')
  const [confirmando, setConfirmando] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setCargando(true)
    const c = await puedoDarDeBaja()
    setChequeo(c)
    setCargando(false)
  }

  async function confirmar() {
    setErr('')
    if (!confirm('¿Seguro? Una vez confirmada, tu cuenta queda cerrada. Si querés volver tenés que escribirnos por WhatsApp.')) return
    setConfirmando(true)
    try {
      const motivoFinal = motivo === 'Otro motivo' ? motivoOtro.trim() : motivo
      await darDeBajaMiCuenta(motivoFinal)
      // Logout + redirect a landing con mensaje
      await supabase.auth.signOut()
      navigate('/login?baja=ok', { replace: true })
    } catch (e) {
      setErr(e?.message || 'No pudimos procesar la baja. Probá de nuevo o escribinos por WhatsApp.')
      setConfirmando(false)
    }
  }

  const puede = !!chequeo?.puede
  const bloqueantes = []
  if (chequeo) {
    if (chequeo.susc_activas > 0) {
      bloqueantes.push({
        icon: '📦',
        titulo: `Tenés ${chequeo.susc_activas} suscripción${chequeo.susc_activas === 1 ? '' : 'es'} activa${chequeo.susc_activas === 1 ? '' : 's'}`,
        desc: 'Primero tenés que cancelar tu suscripción mensual. Eso lo hacés desde "Mi Plan".',
        cta: { to: '/cliente/mi-plan', label: 'Ir a Mi Plan →' },
      })
    }
    if (chequeo.pedido_d1_fecha) {
      bloqueantes.push({
        icon: '🚚',
        titulo: `Tu próximo pedido llega el ${fmtFecha(chequeo.pedido_d1_fecha)}`,
        desc: 'El kit ya está preparándose y sale en las próximas horas. Podés dar de baja una vez que te lo entreguemos.',
        cta: { to: '/cliente/pedidos', label: 'Ver mis pedidos →' },
      })
    }
    if (chequeo.pedidos_pendientes_pago > 0) {
      bloqueantes.push({
        icon: '💳',
        titulo: `Tenés ${chequeo.pedidos_pendientes_pago} pedido${chequeo.pedidos_pendientes_pago === 1 ? '' : 's'} sin pagar`,
        desc: 'Pagá o cancelá los pedidos pendientes desde "Mis pedidos".',
        cta: { to: '/cliente/pedidos', label: 'Ir a Mis pedidos →' },
      })
    }
  }

  return (
    <ClienteLayout title="Dar de baja">
      <div className="db-wrap">
        <div className="db-headline">
          <h1>Dar de baja mi cuenta</h1>
          <p>
            Si querés cerrar tu cuenta, te lo hacemos en 2 clicks. Antes chequeamos
            que no tengas algo pendiente que pueda dejarte colgado.
          </p>
        </div>

        {cargando ? (
          <div className="db-loading">Chequeando tu estado…</div>
        ) : puede ? (
          <div className="db-card">
            <div className="db-warning">
              <div className="db-warning-icon">⚠️</div>
              <div>
                <strong>Esto NO tiene vuelta atrás desde acá.</strong> Si te arrepentís
                después, vas a tener que escribirnos por WhatsApp para reactivar. No
                guardamos tarjetas ni datos de pago — esos van directo al procesador (MercadoPago).
              </div>
            </div>

            <div className="db-section">
              <div className="db-label">¿Por qué te vas? <em>(opcional, nos ayuda a mejorar)</em></div>
              <div className="db-motivos">
                {MOTIVOS_SUGERIDOS.map(m => (
                  <label
                    key={m}
                    className={`db-motivo ${motivo === m ? 'is-active' : ''}`}
                  >
                    <input
                      type="radio"
                      name="motivo"
                      value={m}
                      checked={motivo === m}
                      onChange={(e) => setMotivo(e.target.value)}
                    />
                    <span>{m}</span>
                  </label>
                ))}
              </div>
              {motivo === 'Otro motivo' && (
                <textarea
                  className="db-otro"
                  value={motivoOtro}
                  onChange={(e) => setMotivoOtro(e.target.value)}
                  placeholder="Contanos un poco más (opcional)"
                  rows={3}
                />
              )}
            </div>

            {err && <div className="db-err">{err}</div>}

            <div className="db-actions">
              <Link to="/cliente/cuenta" className="db-cancel">Mejor no, volver</Link>
              <button
                className="db-confirm"
                onClick={confirmar}
                disabled={confirmando}
              >
                {confirmando ? 'Procesando…' : 'Confirmar baja'}
              </button>
            </div>
          </div>
        ) : (
          <div className="db-card">
            <div className="db-blocker-h">No podés dar de baja todavía</div>
            <div className="db-blocker-sub">
              Hay {bloqueantes.length} cosa{bloqueantes.length === 1 ? '' : 's'} pendiente{bloqueantes.length === 1 ? '' : 's'} antes
              de cerrar tu cuenta:
            </div>
            <div className="db-blockers">
              {bloqueantes.map((b, i) => (
                <div className="db-blocker" key={i}>
                  <div className="db-blocker-icon">{b.icon}</div>
                  <div className="db-blocker-body">
                    <div className="db-blocker-titulo">{b.titulo}</div>
                    <div className="db-blocker-desc">{b.desc}</div>
                    <Link to={b.cta.to} className="db-blocker-cta">{b.cta.label}</Link>
                  </div>
                </div>
              ))}
            </div>
            <div className="db-help">
              ¿Tenés dudas? Escribinos por{' '}
              <a href="https://wa.me/59898610001" target="_blank" rel="noreferrer">WhatsApp</a>
              {' '}y te ayudamos.
            </div>
          </div>
        )}
      </div>

      <style>{`
        .db-wrap { max-width: 720px; margin: 0 auto; padding: 16px 16px 32px }
        .db-headline h1 { font-size: 26px; font-weight: 900; color: var(--cl-text); letter-spacing: -.02em }
        .db-headline p { font-size: 14px; color: var(--cl-muted); margin-top: 6px; line-height: 1.55; max-width: 560px }
        .db-loading { text-align: center; padding: 40px; color: var(--cl-muted) }

        .db-card {
          margin-top: 20px; padding: 22px;
          background: var(--cl-card); border: 1px solid var(--cl-line);
          border-radius: 16px;
          display: flex; flex-direction: column; gap: 18px;
        }

        .db-warning {
          display: flex; gap: 12px; align-items: flex-start;
          padding: 14px 16px;
          background: rgba(245,158,11,.10);
          border: 1px solid rgba(245,158,11,.35);
          border-radius: 12px;
          font-size: 13px; color: #fde68a; line-height: 1.55;
        }
        .db-warning strong { color: #fef3c7; font-weight: 800 }
        .db-warning-icon { font-size: 20px; flex-shrink: 0 }

        .db-section { display: flex; flex-direction: column; gap: 10px }
        .db-label { font-size: 12.5px; font-weight: 800; color: var(--cl-text) }
        .db-label em { font-style: normal; font-weight: 500; color: var(--cl-muted) }

        .db-motivos { display: flex; flex-direction: column; gap: 6px }
        .db-motivo {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px;
          background: rgba(0,0,0,.20);
          border: 1.5px solid var(--cl-line);
          border-radius: 10px;
          cursor: pointer; font-size: 13.5px; color: var(--cl-text);
          transition: border-color .12s, background .12s;
        }
        .db-motivo:hover { border-color: var(--cl-line2, rgba(255,255,255,.16)) }
        .db-motivo.is-active { border-color: var(--cl-teal); background: rgba(0,212,224,.08) }
        .db-motivo input { accent-color: var(--cl-teal); width: 16px; height: 16px; flex-shrink: 0 }

        .db-otro {
          padding: 12px 14px;
          background: rgba(0,0,0,.25);
          border: 1px solid var(--cl-line);
          border-radius: 10px;
          color: var(--cl-text); font-size: 14px; font-family: inherit;
          resize: vertical;
        }
        .db-otro:focus { outline: 0; border-color: var(--cl-teal); box-shadow: 0 0 0 3px rgba(0,212,224,.18) }

        .db-err {
          background: rgba(248, 113, 113, .12);
          border: 1px solid rgba(248, 113, 113, .35);
          color: #fca5a5;
          padding: 10px 14px; border-radius: 10px;
          font-size: 13px;
        }

        .db-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 4px }
        .db-cancel {
          padding: 11px 18px; border-radius: 999px;
          background: var(--cl-card); border: 1px solid var(--cl-line);
          color: var(--cl-text); text-decoration: none;
          font-size: 13.5px; font-weight: 700;
        }
        .db-cancel:hover { border-color: var(--cl-line2, rgba(255,255,255,.18)) }
        .db-confirm {
          padding: 11px 22px; border-radius: 999px;
          background: #dc2626; color: #fff; border: 0;
          font-size: 13.5px; font-weight: 800; font-family: inherit;
          cursor: pointer; transition: background .15s, transform .15s;
        }
        .db-confirm:hover { background: #b91c1c; transform: translateY(-1px) }
        .db-confirm:disabled { opacity: .6; cursor: not-allowed }

        .db-blocker-h { font-size: 18px; font-weight: 900; color: var(--cl-text); letter-spacing: -.01em }
        .db-blocker-sub { font-size: 13.5px; color: var(--cl-muted); line-height: 1.55 }
        .db-blockers { display: flex; flex-direction: column; gap: 12px }
        .db-blocker {
          display: flex; gap: 14px; align-items: flex-start;
          padding: 16px;
          background: rgba(245,158,11,.06);
          border: 1px solid rgba(245,158,11,.22);
          border-radius: 12px;
        }
        .db-blocker-icon { font-size: 22px; flex-shrink: 0 }
        .db-blocker-body { flex: 1; min-width: 0 }
        .db-blocker-titulo { font-size: 14px; font-weight: 800; color: var(--cl-text) }
        .db-blocker-desc { font-size: 13px; color: var(--cl-muted); margin-top: 4px; line-height: 1.55 }
        .db-blocker-cta {
          display: inline-block; margin-top: 10px;
          font-size: 13px; font-weight: 800; color: var(--cl-teal); text-decoration: none;
        }
        .db-help { font-size: 12.5px; color: var(--cl-muted); text-align: center; padding-top: 8px }
        .db-help a { color: var(--cl-teal); text-decoration: none; font-weight: 700 }
      `}</style>
    </ClienteLayout>
  )
}
